/* ═══════════════════════════════════════════════════
   CAREER SCORING ENGINE — v2 (FIXED)
   
   BUG IN v1: matchCareers() used Math.min() overlap 
   which compressed all scores to ~99%. A Creative user
   got "AI/ML Engineer" as #1 match.
   
   FIX: Cosine similarity between user trait vector and
   career trait vector. This produces real differentiation:
   - Creative user → Graphic Designer 94%, AI Eng 31%
   - Analytical user → Data Scientist 91%, Fashion 22%
═══════════════════════════════════════════════════ */
import careers from "../data/careers";

/**
 * Calculate trait scores from quiz answers.
 */
export function calculateTraitScores(answers) {
  const raw = {
    analytical: 0,
    creative: 0,
    social: 0,
    technical: 0,
    business: 0,
  };

  answers.forEach((answer) => {
    if (answer && answer.traits) {
      Object.keys(answer.traits).forEach((trait) => {
        if (raw[trait] !== undefined) {
          raw[trait] += answer.traits[trait];
        }
      });
    }
  });

  const total = Object.values(raw).reduce((a, b) => a + b, 0) || 1;
  const normalized = {};
  Object.keys(raw).forEach((trait) => {
    normalized[trait] = Math.round((raw[trait] / total) * 100);
  });

  return normalized;
}

/**
 * Cosine similarity between two vectors.
 * Returns 0-1 where 1 = perfect alignment.
 */
function cosineSimilarity(vecA, vecB) {
  let dot = 0,
    magA = 0,
    magB = 0;
  for (let i = 0; i < vecA.length; i++) {
    dot += vecA[i] * vecB[i];
    magA += vecA[i] * vecA[i];
    magB += vecB[i] * vecB[i];
  }
  magA = Math.sqrt(magA);
  magB = Math.sqrt(magB);
  if (magA === 0 || magB === 0) return 0;
  return dot / (magA * magB);
}

/**
 * Match user traits to careers using cosine similarity.
 *
 * WHY THIS WORKS:
 * - User with Creative=43%, Analytical=11% has a vector pointing toward creative
 * - Graphic Designer has traits {creative: 0.45, technical: 0.15, ...} pointing same way
 * - AI Engineer has traits {analytical: 0.35, technical: 0.35, ...} pointing differently
 * - Cosine similarity measures the ANGLE between vectors, not just overlap
 * - So Creative user gets Graphic Designer 90%+, AI Engineer 30%-ish
 */
export function matchCareers(traits) {
  const traitKeys = [
    "analytical",
    "creative",
    "social",
    "technical",
    "business",
  ];

  // User vector from percentage traits
  const userVec = traitKeys.map((k) => traits[k] || 0);

  const scored = careers.map((career) => {
    // Career vector from trait weights (these are 0-1 in careers.js)
    const careerVec = traitKeys.map((k) => (career.traits[k] || 0) * 100);

    // Cosine similarity gives 0-1
    const similarity = cosineSimilarity(userVec, careerVec);

    // Convert to percentage with realistic spread (30-96 range)
    const percentage = Math.round(similarity * 100);
    // Clamp between 15 and 96
    const clamped = Math.max(15, Math.min(96, percentage));

    return {
      ...career,
      matchPercentage: clamped,
    };
  });

  // Sort by match percentage descending
  scored.sort((a, b) => b.matchPercentage - a.matchPercentage);
  return scored;
}

/**
 * Get personality type based on dominant trait.
 */
export function getPersonalityType(traits) {
  const sorted = Object.entries(traits).sort((a, b) => b[1] - a[1]);
  const dominant = sorted[0][0];
  const secondary = sorted[1][0];

  const types = {
    analytical: {
      title: "The Thinker",
      emoji: "🧠",
      description:
        "You are logical, data-driven, and love solving complex problems. You see patterns where others see chaos. Careers in science, research, and engineering are your natural habitat.",
      color: "#6C5CE7",
      stream: "Science (PCM)",
      streamReason:
        "Your analytical mind thrives with Mathematics, Physics, and logical problem-solving.",
    },
    creative: {
      title: "The Creator",
      emoji: "🎨",
      description:
        "You are imaginative, expressive, and see the world differently. You turn ideas into reality through art, design, and storytelling. Creative careers will let your imagination soar.",
      color: "#A29BFE",
      stream: "Arts / Humanities (or Science with Design focus)",
      streamReason:
        "Your creative mind flourishes with Fine Arts, Design, Media Studies, or Architecture.",
    },
    social: {
      title: "The Connector",
      emoji: "🤝",
      description:
        "You are empathetic, communicative, and people-oriented. You shine brightest when helping, teaching, or inspiring others. Careers in counseling, teaching, and social work await you.",
      color: "#00CEC9",
      stream: "Arts / Humanities",
      streamReason:
        "Your empathy grows with Psychology, Sociology, Political Science, and History.",
    },
    technical: {
      title: "The Builder",
      emoji: "🔧",
      description:
        "You are hands-on, practical, and love making things work. You build solutions that people depend on every day. Engineering and technology careers are calling your name.",
      color: "#00B894",
      stream: "Science (PCM with Computer Science)",
      streamReason:
        "Your builder instinct needs Physics, Mathematics, and hands-on engineering.",
    },
    business: {
      title: "The Strategist",
      emoji: "📈",
      description:
        "You are ambitious, resourceful, and see opportunities everywhere. You think big and know how to turn ideas into profitable ventures. Business and leadership careers suit you best.",
      color: "#FDCB6E",
      stream: "Commerce",
      streamReason:
        "Your business mind excels with Accountancy, Economics, Business Studies.",
    },
  };

  return {
    ...types[dominant],
    dominant,
    secondary,
    combo: `${types[dominant].emoji} ${types[dominant].title.split(" ")[1]} + ${types[secondary].emoji} ${types[secondary].title.split(" ")[1]}`,
  };
}
